import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Check, Loader2 } from "lucide-react";
import HttpService from "../../hooks/Https-services";
import PageRoutes, { APIRoutes } from "../../utils/constants";
import useConfig from "../../hooks/useConfig";
import { Logout } from "../../hooks/useAxiosPrivate";
import { V2Shell } from "@/v2/components/V2Shell";
import { Button } from "@/v2/components/ui/button";

// HTPTV-636: recipient redeem/activation landing page (public). The gift binds
// only to the invited email; the backend enforces that on redeem, so new
// recipients sign up with their address and existing ones log in with it.
const REASON_MESSAGES: Record<string, string> = {
  already_redeemed: "This gift has already been activated.",
  expired: "This gift has expired.",
  not_delivered: "This gift isn't available to activate yet.",
};

const TIER_DESC: Record<string, string> = {
  basic: "Every game, live and on demand, across all leagues.",
  insider:
    "All games plus personalized channels, watch parties and clip downloads.",
};

const termLong = (billing: string, term: number) => {
  const unit = billing === "annual" ? "year" : "month";
  return `${term} ${unit}${term > 1 ? "s" : ""}`;
};

const termShort = (billing: string, term: number) => {
  const unit = billing === "annual" ? "YR" : "MO";
  return `${term} ${unit}${term > 1 ? "S" : ""}`;
};

const RedeemGift = () => {
  const { token }: any = useParams();
  const navigate = useNavigate();
  const configData = useConfig();

  const [loading, setLoading] = useState(true);
  const [gift, setGift] = useState<any>(null);
  const [reason, setReason] = useState<string>("");
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneDate, setDoneDate] = useState<string | null>(null);
  // HTPTV-636: redeemed while the recipient already had coverage (a paid plan
  // or an earlier gift) -> the gift is queued and starts when that coverage ends.
  const [queued, setQueued] = useState(false);
  // HTPTV-636: when a logged-in user views the page, the backend reports whether
  // the gift is addressed to their email (null = not logged in). false -> hide
  // the Activate button (it would only 403) and tell them to use the right account.
  const [forCurrentUser, setForCurrentUser] = useState<boolean | null>(null);
  // HTPTV-636 Option B: if the recipient already has an active paid plan,
  // activating the gift stops that plan from auto-renewing and the gift takes
  // over when the current paid period ends. We ask for consent first.
  const [hasPaidPlan, setHasPaidPlan] = useState(false);
  const [planRenewsOn, setPlanRenewsOn] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [startsOn, setStartsOn] = useState<string | null>(null);

  const isLoggedIn = (() => {
    try {
      return Boolean(
        JSON.parse(localStorage.getItem("auth") || "{}")?.AccessToken,
      );
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    (async () => {
      try {
        const res = await HttpService.get(
          APIRoutes.GetGiftRedeemInfo.replace(":token", token),
        );
        const d = res?.data?.data;
        setGift(d?.gift || null);
        setForCurrentUser(
          d?.forCurrentUser === undefined ? null : d.forCurrentUser,
        );
        setHasPaidPlan(Boolean(d?.viewer?.hasActivePaidPlan));
        setPlanRenewsOn(d?.viewer?.planRenewsOn || null);
        if (!d?.redeemable) setReason(d?.reason || "unavailable");
      } catch (e: any) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleRedeem = async () => {
    setSubmitting(true);
    try {
      const res = await HttpService.post(
        APIRoutes.RedeemGiftSubscription.replace(":token", token),
        {},
      );
      const d = res?.data?.data;
      setQueued(Boolean(d?.queued));
      setStartsOn(d?.startsOn || null);
      setDoneDate(d?.entitlementEnd || "");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? "Unable to activate this gift.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Plain centered card for loading / error / success states (no hero banner).
  const PlainShell = ({ children }: { children: React.ReactNode }) => (
    <V2Shell>
      {/* V2Shell locks body to height:100vh;overflow:hidden, so this page owns
          its own scroll container - otherwise tall content (e.g. the paid-plan
          consent state on a short screen) is clipped and unreachable. */}
      <div className="h-screen w-full overflow-y-auto bg-muted/40">
        <div
          className="min-h-full flex items-center justify-center"
          style={{ padding: "16px" }}
        >
          <div
            className="bg-card border border-border rounded-2xl shadow-sm w-full max-w-[480px] text-center"
            style={{ padding: "32px" }}
          >
            {children}
          </div>
        </div>
      </div>
    </V2Shell>
  );

  if (loading) {
    return (
      <PlainShell>
        <div className="flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-action" />
        </div>
      </PlainShell>
    );
  }

  if (notFound) {
    return (
      <PlainShell>
        <div className="flex flex-col" style={{ gap: "6px" }}>
          <h1 className="text-lg font-extrabold text-foreground">
            Gift not found
          </h1>
          <p className="text-sm text-muted-foreground">
            This link is invalid. Please check the link in your email.
          </p>
        </div>
      </PlainShell>
    );
  }

  if (doneDate !== null) {
    return (
      <PlainShell>
        <div className="flex flex-col items-center" style={{ gap: "16px" }}>
          <div className="size-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
            <Check className="size-7 stroke-[3]" />
          </div>
          <div className="flex flex-col" style={{ gap: "6px" }}>
            <h1 className="text-lg font-extrabold text-foreground">
              You're all set!
            </h1>
            <p className="text-sm text-muted-foreground">
              {queued
                ? `Your gift is saved. ${
                    hasPaidPlan
                      ? "Your current plan won't renew, and your"
                      : "Your"
                  } gift starts ${
                    startsOn
                      ? `on ${dayjs(startsOn).format("MMM D, YYYY")}`
                      : "when your current access ends"
                  }.`
                : `Your access is active${
                    doneDate
                      ? ` through ${dayjs(doneDate).format("MMM D, YYYY")}`
                      : ""
                  }. Enjoy the games.`}
            </p>
          </div>
          <Button
            className="w-full h-11 bg-action text-white hover:bg-action-hover font-bold"
            onClick={() => navigate(PageRoutes.HomePage)}
          >
            {queued ? "Go to home" : "Start watching"}
          </Button>
        </div>
      </PlainShell>
    );
  }

  if (reason) {
    return (
      <PlainShell>
        <div className="flex flex-col" style={{ gap: "6px" }}>
          <h1 className="text-lg font-extrabold text-foreground">
            Gift unavailable
          </h1>
          <p className="text-sm text-muted-foreground">
            {REASON_MESSAGES[reason] || "This gift can't be activated."}
          </p>
        </div>
      </PlainShell>
    );
  }

  const tier = gift?.planTier
    ? gift.planTier.charAt(0).toUpperCase() + gift.planTier.slice(1)
    : "";
  const isWrongAccount = isLoggedIn && forCurrentUser === false;

  return (
    <V2Shell>
      {/* Own scroll container; V2Shell locks body to height:100vh;overflow:hidden. */}
      <div className="h-screen w-full overflow-y-auto bg-muted/40">
        <div
          className="min-h-full flex items-center justify-center"
          style={{ padding: "16px" }}
        >
          <div className="bg-card border border-border rounded-2xl shadow-sm w-full max-w-[560px] overflow-hidden">
            {/* Hero banner */}
          <div
            className="relative overflow-hidden text-center"
            style={{
              background: "linear-gradient(135deg, #15264d 0%, #0e1a35 100%)",
              paddingLeft: "24px",
              paddingRight: "24px",
              paddingTop: "34px",
              paddingBottom: "34px",
            }}
          >
            <div
              className="absolute rounded-full"
              style={{
                top: -40,
                right: -30,
                width: 160,
                height: 160,
                background: "rgba(255,255,255,0.05)",
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                bottom: -50,
                left: -20,
                width: 140,
                height: 140,
                background: "rgba(255,255,255,0.04)",
              }}
            />
            <p
              className="font-bold uppercase"
              style={{
                color: "rgba(255,255,255,0.85)",
                letterSpacing: "3px",
                fontSize: "0.8rem",
              }}
            >
              {isWrongAccount
                ? `${configData?.personalChannel} GIFT`
                : "CONGRATULATIONS"}
            </p>
            <div
              className="leading-none"
              style={{ fontSize: "2.6rem", marginTop: "12px", marginBottom: "12px" }}
            >
              🎁
            </div>
            <div className="flex flex-row items-center justify-center gap-2.5">
              <span
                className="font-extrabold text-white"
                style={{ fontSize: "1.6rem", lineHeight: 1.15 }}
              >
                {isWrongAccount ? "This gift is for" : "Welcome to"}
              </span>
              {!isWrongAccount && configData?.logoUrl && (
                <span
                  className="bg-white rounded-lg flex items-center"
                  style={{ padding: "6px 10px" }}
                >
                  <img
                    src={configData.logoUrl}
                    alt={configData.personalChannel}
                    style={{ height: 26, objectFit: "contain", maxWidth: 110 }}
                  />
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          <div
            className="text-center flex flex-col"
            style={{ padding: "28px 24px", gap: "20px" }}
          >
            {isWrongAccount ? (
              <p className="text-sm text-foreground leading-relaxed">
                This gift is for a different account. It was sent to a different
                email address; sign in with that address to activate it.
              </p>
            ) : (
              <p className="text-sm text-foreground leading-relaxed">
                You've been gifted a{" "}
                <strong>
                  {termLong(gift?.billingModel, gift?.termCount || 1)} {tier}{" "}
                  subscription
                </strong>
                : every game, live and on demand.
              </p>
            )}

            {/* Plan card */}
            <div
              className="flex items-center gap-4 border border-border rounded-xl bg-muted/40 text-left"
              style={{ padding: "16px" }}
            >
              <div
                className="flex-shrink-0 border border-border rounded-lg bg-white text-center"
                style={{ width: 64, padding: "6px" }}
              >
                {configData?.logoUrl && (
                  <img
                    src={configData.logoUrl}
                    alt={configData.personalChannel}
                    style={{
                      height: 22,
                      objectFit: "contain",
                      maxWidth: "100%",
                    }}
                  />
                )}
                <div
                  className="font-extrabold text-action"
                  style={{
                    marginTop: "4px",
                    fontSize: "0.55rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  {(gift?.planTier || "").toUpperCase()} ·{" "}
                  {termShort(gift?.billingModel, gift?.termCount || 1)}
                </div>
              </div>
              <div className="flex-1">
                <p className="font-extrabold text-action">{tier} Plan</p>
                <p className="text-xs text-muted-foreground leading-snug">
                  {TIER_DESC[gift?.planTier] || ""}
                </p>
              </div>
              {gift?.buyerName && (
                <div className="flex-shrink-0 text-right">
                  <p
                    className="text-xs text-muted-foreground"
                    style={{ letterSpacing: "1px" }}
                  >
                    FROM
                  </p>
                  <p className="font-bold text-sm text-foreground">
                    {gift.buyerName}
                  </p>
                </div>
              )}
            </div>

            {/* Personal message */}
            {gift?.personalMessage && !isWrongAccount && (
              <div
                className="text-left"
                style={{
                  borderLeft: "3px solid var(--v2-action)",
                  paddingLeft: "16px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
              >
                <p
                  className="text-sm text-muted-foreground"
                  style={{ fontStyle: "italic" }}
                >
                  "{gift.personalMessage}"
                </p>
              </div>
            )}

            {/* Action area */}
            {isWrongAccount ? (
              <Button
                variant="outline"
                className="w-full h-11"
                style={{
                  color: "var(--v2-action)",
                  borderColor: "var(--v2-action)",
                }}
                onClick={() => {
                  Logout();
                  navigate(PageRoutes.Login, {
                    state: {
                      from: window.location.pathname,
                      search: window.location.search,
                    },
                  });
                }}
              >
                Use a different account
              </Button>
            ) : isLoggedIn ? (
              confirming ? (
                // Option B consent: recipient already has an active paid plan.
                <div className="flex flex-col" style={{ gap: "12px" }}>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    You currently have an active plan. Activating this gift will
                    stop it from auto-renewing, so you won't be charged again.
                    Your gift begins{" "}
                    {planRenewsOn
                      ? `on ${dayjs(planRenewsOn).format("MMM D, YYYY")}`
                      : "when your current plan period ends"}{" "}
                    and runs for{" "}
                    {termLong(gift?.billingModel, gift?.termCount || 1)}.
                  </p>
                  <Button
                    disabled={submitting}
                    className="w-full h-11 bg-action text-white hover:bg-action-hover font-bold"
                    onClick={handleRedeem}
                  >
                    {submitting ? "Activating..." : "Yes, activate my gift"}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={submitting}
                    className="w-full text-muted-foreground"
                    onClick={() => setConfirming(false)}
                  >
                    Keep my current plan for now
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col" style={{ gap: "16px" }}>
                  <Button
                    disabled={submitting}
                    className="w-full h-12 bg-action text-white hover:bg-action-hover font-bold text-base"
                    onClick={() =>
                      hasPaidPlan ? setConfirming(true) : handleRedeem()
                    }
                  >
                    {submitting ? "Activating..." : "Activate My Subscription"}
                  </Button>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {hasPaidPlan
                      ? "You already have a plan. Activating uses your gift and stops that plan from renewing, so you won't be charged again."
                      : "No payment needed: your gift is already paid for. We'll remind you before it ends, with the option to keep watching."}
                  </p>
                </div>
              )
            ) : (
              <div className="flex flex-col" style={{ gap: "12px" }}>
                <Button
                  className="w-full h-11 bg-action text-white hover:bg-action-hover font-bold"
                  onClick={() =>
                    navigate(
                      `${PageRoutes.Register}?giftToken=${encodeURIComponent(token)}`,
                    )
                  }
                >
                  Create an account to activate
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-11"
                  style={{
                    color: "var(--v2-action)",
                    borderColor: "var(--v2-action)",
                  }}
                  onClick={() =>
                    navigate(PageRoutes.Login, {
                      state: {
                        from: window.location.pathname,
                        search: window.location.search,
                      },
                    })
                  }
                >
                  I already have an account
                </Button>
                <p className="text-xs text-muted-foreground">
                  Use the email address this gift was sent to.
                </p>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </V2Shell>
  );
};

export default RedeemGift;
